import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { ModalBody } from "reactstrap";

import {
    setStandAlonePrintingStationPrints,
    setPrintingOptionsSpecialCharacters,
    setPendingBarcodeChecked,
    setPendingBarcodePosition,
    setPendingQrCodeChecked,
    setPendingQrCodePosition,
    setOrderBarcodeChecked,
    setOrderBarcodePosition,
    setOrderQrCodeChecked,
    setOrderQrCodePosition,
    setPaperlessTicket,
    setQrCodeType,
    setQrCodeUrl,
    setQrCodeImage,
    storeDivers,
    setDivers,
} from "@components/Common/PrinterSetting/stores";

import { POSITION_BOTTOM } from "@constants/index";

import { PrinterRowData, PrinterDiversBlock } from "../../types";

import UserCard from "./Hook/UserCard";

import "@assets/swal.css";
import "@assets/theme.css";
import "../index.css";

type DiversType = {
    rowData: PrinterRowData;
    oneShop?: boolean;
};

export default function Divers({ rowData }: DiversType) {
    const { t } = useTranslation();
    const {
        printingOptionsSpecialCharacters,
        pendingBarcodeChecked,
        pendingBarcodePosition,
        pendingQrCodeChecked,
        pendingQrCodePosition,
        orderBarcodeChecked,
        orderBarcodePosition,
        orderQrCodeChecked,
        orderQrCodePosition,
        paperlessTicket,
        qrCodeType,
        qrCodeUrl,
        qrCodeImage,
    } = useSnapshot(storeDivers);

    useEffect(() => {
        const divers = rowData["divers"];
        if (divers !== undefined)
            if (Object.keys(divers).length > 0) {
                if (divers.alone_printing) {
                    setStandAlonePrintingStationPrints(
                        divers.alone_printing.station_prints ?? false
                    );
                }
                if (divers.printing_options) {
                    setPrintingOptionsSpecialCharacters(
                        divers.printing_options.special_characters ?? false
                    );
                }

                if (divers.Code) {
                    const code = divers.Code;
                    if (code.pending !== undefined) {
                        setPendingBarcodeChecked(
                            code.pending.barcodeChecked ?? false
                        );
                        setPendingBarcodePosition(
                            code.pending.barcodePosition ?? POSITION_BOTTOM
                        );
                        setPendingQrCodeChecked(
                            code.pending.qrCodeChecked ?? false
                        );
                        setPendingQrCodePosition(
                            code.pending.qrCodePosition ?? POSITION_BOTTOM
                        );
                    }
                    if (code.order !== undefined) {
                        setOrderBarcodeChecked(
                            code.order.barcodeChecked ?? false
                        );
                        setOrderBarcodePosition(
                            code.order.barcodePosition ?? POSITION_BOTTOM
                        );
                        setOrderQrCodeChecked(
                            code.order.qrCodeChecked ?? false
                        );
                        setOrderQrCodePosition(
                            code.order.qrCodePosition ?? POSITION_BOTTOM
                        );
                        if (code.order.qr_code_config) {
                            setQrCodeType(
                                code.order.qr_code_config.type || "url"
                            );
                            setQrCodeUrl(code.order.qr_code_config.url || "");
                            setQrCodeImage(
                                code.order.qr_code_config.image || ""
                            );
                        }
                    }
                }
                // Always assign to avoid the proxy default (false) leaking
                // into setDivers when rowData.divers.paperless_ticket is
                // missing or nullish on legacy un-migrated docs.
                setPaperlessTicket(
                    divers.paperless_ticket?.paperless_ticket_custom ?? false
                );
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const data: PrinterDiversBlock = {
            printing_options: {
                special_characters: printingOptionsSpecialCharacters,
            },
            Code: {
                pending: {
                    barcodeChecked: pendingBarcodeChecked,
                    barcodePosition: pendingBarcodePosition,
                    qrCodeChecked: pendingQrCodeChecked,
                    qrCodePosition: pendingQrCodePosition,
                    qr_code_config: {
                        type: "",
                        url: "",
                        image: "",
                    },
                },
                order: {
                    barcodeChecked: orderBarcodeChecked,
                    barcodePosition: orderBarcodePosition,
                    qrCodeChecked: orderQrCodeChecked,
                    qrCodePosition: orderQrCodePosition,
                    qr_code_config: {
                        type: qrCodeType,
                        url: qrCodeUrl,
                        image: qrCodeImage,
                    },
                },
            },
            paperless_ticket: {
                paperless_ticket_custom: paperlessTicket,
            },
        };
        setDivers(data);
    }, [
        printingOptionsSpecialCharacters,
        pendingBarcodeChecked,
        pendingBarcodePosition,
        pendingQrCodeChecked,
        pendingQrCodePosition,
        orderBarcodeChecked,
        orderBarcodePosition,
        orderQrCodeChecked,
        orderQrCodePosition,
        paperlessTicket,
        qrCodeType,
        qrCodeUrl,
        qrCodeImage,
    ]);

    return (
        <React.Fragment>
            <ModalBody style={{ height: "730px", overflow: "hidden" }}>
                <div className="px-3 pt-2">
                    <h4 style={{ fontSize: "24px" }}>{t("Various")}</h4>
                </div>
                <UserCard />
            </ModalBody>
        </React.Fragment>
    );
}
